import Dialog from '../../../organisms/Dialog';
import { formatBRL } from '../../../../utils/currencyUtils';
import { SummaryGroup, breakdownInvoices, summarizeInvoices } from '../services/invoiceSummary';

export const SELECTION_SUMMARY_ID = 'invoice-selection-summary';

const ROW_GRID = 'grid grid-cols-[1fr_40px_112px] items-baseline gap-x-2';

const SECTION_TITLE = 'text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';

const SingleValue: React.FC<{ title: string; label: string }> = ({ title, label }) => (
    <section className="grid grid-cols-[72px_1fr] items-baseline gap-x-2 py-1.5 border-b border-dotted border-slate-200 dark:border-slate-600">
        <h4 className={SECTION_TITLE}>{title}</h4>
        <span className="truncate font-semibold text-gray-800 dark:text-gray-200" title={label}>{label}</span>
    </section>
);

const GroupSection: React.FC<{ title: string; groups: SummaryGroup[] }> = ({ title, groups }) => {
    if (groups.length === 1) return <SingleValue title={title} label={groups[0].label} />;
    return (
        <section className="py-1.5 border-b border-dotted border-slate-200 dark:border-slate-600">
            <h4 className={SECTION_TITLE}>
                {title} <span className="font-normal">({groups.length})</span>
            </h4>
            {groups.map(group => (
                <div key={group.label} className={`${ROW_GRID} py-0.5`}>
                    <span className="truncate text-gray-800 dark:text-gray-200" title={group.label}>{group.label}</span>
                    <span className="text-right text-gray-500 dark:text-gray-400">{group.count}</span>
                    <span className="text-right font-semibold text-gray-900 dark:text-gray-100">{formatBRL(group.titleAmount)}</span>
                </div>
            ))}
        </section>
    );
};

interface Props {
    rows: any[];
    onClose: () => void;
}

const InvoiceSelectionSummary: React.FC<Props> = ({ rows, onClose }) => {
    const summary = summarizeInvoices(rows);
    const breakdown = breakdownInvoices(rows);
    return (
        <Dialog
            id={SELECTION_SUMMARY_ID}
            show
            modeless
            placement="bottom-right"
            size="sm"
            onClose={onClose}
            header={`${summary.count} ${summary.count === 1 ? 'fatura selecionada' : 'faturas selecionadas'}`}
            body={(
                <div className="text-xs">
                    <div className="px-3 max-h-[min(590px,calc(100vh-220px))] overflow-y-auto">
                        <div className={`${ROW_GRID} sticky top-0 z-10 pt-2 pb-1 text-[10px] uppercase text-gray-400 bg-white dark:bg-slate-800 border-b dark:border-slate-600`}>
                            <span />
                            <span className="text-right">Qtd.</span>
                            <span className="text-right">Valor Título</span>
                        </div>
                        <GroupSection title="Natureza" groups={breakdown.natures} />
                        <GroupSection title="Status" groups={breakdown.statuses} />
                        <GroupSection title="Cobrar" groups={breakdown.collect} />
                        <GroupSection title="Operação" groups={breakdown.operations} />
                        <GroupSection title="Emitente" groups={breakdown.issuers} />
                        <GroupSection title="Devedor" groups={breakdown.payers} />
                    </div>
                    <div className="px-3 py-2 border-t dark:border-slate-600 bg-slate-50 dark:bg-slate-900/40 rounded-b-lg">
                        <div className={ROW_GRID}>
                            <span className="font-semibold text-gray-700 dark:text-gray-200">Total</span>
                            <span className="text-right text-gray-500 dark:text-gray-400">{summary.count}</span>
                            <strong className="text-right text-gray-900 dark:text-gray-100">{formatBRL(summary.titleAmount)}</strong>
                        </div>
                        <div className="flex justify-between pt-1">
                            <span className="text-gray-500 dark:text-gray-400">Valor Fatura</span>
                            <strong className="text-sm text-gray-900 dark:text-gray-100">{formatBRL(summary.invoiceAmount)}</strong>
                        </div>
                    </div>
                </div>
            )}
        />
    );
};

export default InvoiceSelectionSummary;
